<template>
  <vs-popup
    class="calendar-event-dialog"
    :title="event.title"
    :active.sync="isVisible">
    <div class="event-details-container">
      <!-- Status Badge -->
      <div class="status-header" v-if="event.status">
        <b-badge :variant="statusColor" pill class="status-badge">
          {{ event.status.label }}
        </b-badge>
      </div>

      <!-- Event Details -->
      <div class="details-section">
        <!-- Instituição -->
        <div class="detail-item">
          <div class="icon-wrapper bg-blue-50">
            <feather-icon icon="HomeIcon" class="detail-icon text-primary" />
          </div>
          <div class="detail-content">
            <span class="detail-label">Instituição</span>
            <span class="detail-value">{{ event.legal_entity_name }}</span>
          </div>
        </div>

        <!-- Endereço -->
        <div class="detail-item" v-if="event.address">
          <div class="icon-wrapper bg-red-50">
            <feather-icon icon="MapPinIcon" class="detail-icon text-danger" />
          </div>
          <div class="detail-content">
            <span class="detail-label">Endereço</span>
            <span class="detail-value">{{ event.address }}</span>
          </div>
        </div>

        <!-- Data -->
        <div class="detail-item">
          <div class="icon-wrapper bg-purple-50">
            <feather-icon icon="CalendarIcon" class="detail-icon text-purple-600" />
          </div>
          <div class="detail-content">
            <span class="detail-label">Data</span>
            <span class="detail-value">{{ formatDate(event.startDate) }}</span>
          </div>
        </div>

        <!-- Horário -->
        <div class="detail-item">
          <div class="icon-wrapper bg-green-50">
            <feather-icon icon="ClockIcon" class="detail-icon text-success" />
          </div>
          <div class="detail-content">
            <span class="detail-label">Horário</span>
            <span class="detail-value">
              {{ formatTime(event.startDate) }} - {{ formatTime(event.endDate) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="actions-section mt-4" v-if="canShowActions">
        <b-button
          v-if="canConfirmEvent"
          v-permission="getPermission('confirm')"
          variant="success"
          class="action-button action-button-confirm"
          @click="handleConfirm">
          <feather-icon icon="CheckCircleIcon" class="action-icon" />
          <span class="action-text">{{ $t('lesson.confirm_completion') }}</span>
        </b-button>
        <b-button
          v-if="canRescheduleEvent"
          v-permission="getPermission('reschedule')"
          variant="primary"
          class="action-button action-button-reschedule"
          @click="handleReschedule">
          <feather-icon icon="CalendarIcon" class="action-icon" />
          <span class="action-text">{{ $t('lesson.reschedule') }}</span>
        </b-button>
        <b-button
          v-if="canCancelEvent"
          v-permission="getPermission('cancel')"
          variant="danger"
          class="action-button action-button-cancel"
          @click="handleCancel">
          <feather-icon icon="XCircleIcon" class="action-icon" />
          <span class="action-text">{{ $t('lesson.cancel_schedule') }}</span>
        </b-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import { BButton, BBadge } from 'bootstrap-vue'

export default {
  name: 'EventDetailsPopup',
  components: {
    BButton,
    BBadge
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    event: {
      type: Object,
      default: () => ({
        id: null,
        title: '',
        legal_entity_name: '',
        address: '',
        startDate: '',
        endDate: '',
        status: null
      })
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    statusColor() {
      // Variants válidos do Bootstrap-Vue: primary, secondary, success, danger, warning, info, light, dark
      const validVariants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']

      // Se não tem status, retorna secondary como padrão
      if (!this.event.status) {
        return 'secondary'
      }

      // Se tem cor no status e é válida, usa ela diretamente
      if (this.event.status.color && validVariants.includes(this.event.status.color)) {
        return this.event.status.color
      }

      // Mapeia cores customizadas para variants do Bootstrap-Vue
      const colorMap = {
        'grey': 'secondary',
        'gray': 'secondary',
        'green': 'success',
        'red': 'danger',
        'yellow': 'warning',
        'blue': 'primary',
        'orange': 'warning'
      }

      if (this.event.status.color && colorMap[this.event.status.color]) {
        return colorMap[this.event.status.color]
      }

      // Padrão para outros status sem cor ou cor inválida
      return 'secondary'
    },
    canShowActions() {
      return this.event.status && this.event.status.key
    },
    canConfirmEvent() {
      if (!this.event.status || !this.event.status.key) return false
      const notAllowedStatuses = ['DO', 'CA']
      return !notAllowedStatuses.includes(this.event.status.key)
    },
    canRescheduleEvent() {
      if (!this.event.status || !this.event.status.key) return false
      const notAllowedStatuses = ['RE', 'CA', 'DO']
      return !notAllowedStatuses.includes(this.event.status.key)
    },
    canCancelEvent() {
      if (!this.event.status || !this.event.status.key) return false
      const notAllowedStatuses = ['CA', 'DO']
      return !notAllowedStatuses.includes(this.event.status.key)
    }
  },
  methods: {
    formatDate(timestamp) {
      return this.$utils.format.longDate(timestamp)
    },
    formatTime(timestamp) {
      return this.$utils.format.shortTime(timestamp)
    },
    getPermission(action) {
      // Determina a permissão baseada no tipo de usuário/contexto
      return (`teacher_my_lessons.${action}`)
    },
    handleConfirm() {
      this.$emit('confirm', this.event)
      this.$nextTick(() => {
        this.isVisible = false
      })
    },
    handleReschedule() {
      this.$emit('reschedule', this.event)
      this.$nextTick(() => {
        this.isVisible = false
      })
    },
    handleCancel() {
      this.$emit('cancel', this.event)
      this.$nextTick(() => {
        this.isVisible = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// Modern Event Details Styling
.event-details-container {
  padding: 0.5rem 0;
}

.status-header {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f1f1;

  .status-badge {
    font-size: 0.875rem !important;
    font-weight: 600;
    padding: 0.5rem 1.5rem;
    letter-spacing: 0.025em;
    text-transform: uppercase;
  }
}

.details-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #fafafa;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  border-radius: 10px;
  transition: all 0.2s ease;

  .detail-icon {
    width: 20px;
    height: 20px;
  }
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 0;
}

.detail-label {
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.detail-value {
  font-size: 0.95rem;
  color: #374151;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
}

// Dark mode support
.theme-dark {
  .detail-item {
    background: rgba(255, 255, 255, 0.05);

    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .detail-label {
    color: #9ca3af;
  }

  .detail-value {
    color: #e5e7eb;
  }

  .status-header {
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }
}

// Background color utilities
.bg-blue-50 {
  background-color: #eff6ff;
}

.bg-red-50 {
  background-color: #fef2f2;
}

.bg-purple-50 {
  background-color: #faf5ff;
}

.bg-green-50 {
  background-color: #f0fdf4;
}

.text-purple-600 {
  color: #9333ea;
}

// Actions Section
.actions-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f1f1;

  .action-button {
    flex: 1 1 auto;
    min-width: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 500;
    padding: 0.75rem 1.25rem;
    transition: all 0.2s ease;
    white-space: nowrap;
    border-radius: 8px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    &:active {
      transform: translateY(0);
    }

    .action-icon {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    .action-text {
      font-size: 0.95rem;
      line-height: 1;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .actions-section {
    gap: 0.625rem;

    .action-button {
      min-width: 120px;
      padding: 0.65rem 1rem;

      .action-icon {
        width: 16px;
        height: 16px;
      }

      .action-text {
        font-size: 0.875rem;
      }
    }
  }
}

@media (max-width: 640px) {
  .detail-item {
    padding: 0.875rem;
  }

  .icon-wrapper {
    min-width: 36px;
    min-height: 36px;

    .detail-icon {
      width: 18px;
      height: 18px;
    }
  }

  .detail-label {
    font-size: 0.7rem;
  }

  .detail-value {
    font-size: 0.875rem;
  }

  .actions-section {
    flex-direction: column;
    gap: 0.5rem;

    .action-button {
      flex: 1 1 100%;
      width: 100%;
      min-width: unset;
      padding: 0.875rem 1rem;
      font-size: 0.95rem;

      .action-icon {
        width: 18px;
        height: 18px;
      }

      .action-text {
        font-size: 0.9rem;
        font-weight: 600;
      }
    }
  }
}

@media (max-width: 380px) {
  .actions-section {
    .action-button {
      padding: 0.75rem 0.875rem;

      .action-icon {
        width: 16px;
        height: 16px;
      }

      .action-text {
        font-size: 0.85rem;
      }
    }
  }
}
</style>

